import { useState } from 'react';
import { View, Text, ScrollView, StyleSheet } from 'react-native';
import { router, type Href } from 'expo-router';
import Head from 'expo-router/head';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { CheckCircle2, Link2Off, TriangleAlert } from 'lucide-react-native';
import { useTheme, SPACING, TYPOGRAPHY, BORDER_RADIUS, READING_MAX_WIDTH } from '../../theme';
import { EmptyState, LoadingSpinner, RediLogo, TextLink } from '@components/common';
import { BidForm } from './BidForm';
import type { PublicPlanBundle } from '../../hooks/usePublicPlanBundle';

/** How many addresses the page lists before it says "and N more". */
const ADDRESS_LIMIT = 8;

function Header() {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  return (
    <View
      style={[
        styles.header,
        { paddingTop: insets.top + SPACING.md, borderBottomColor: colors.border },
      ]}
      testID="opportunity-header">
      <View style={styles.headerInner}>
        <RediLogo fontSize={20} />
      </View>
    </View>
  );
}

function Shell({ title, children }: { title: string; children: React.ReactNode }) {
  const { colors } = useTheme();
  return (
    <View
      style={[styles.container, { backgroundColor: colors.background }]}
      testID="opportunity-page">
      <Head>
        <title>{title}</title>
      </Head>
      <Header />
      {children}
    </View>
  );
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  const { colors } = useTheme();
  return (
    <View style={styles.detail}>
      <Text style={[styles.overline, { color: colors.textSecondary }]}>{label}</Text>
      {children}
    </View>
  );
}

/**
 * /opportunity/<planId>/<bundleId>: a bundle of wildfire defense work that its
 * plan owner opened for vendor bids (Oct 2026). A vendor with the link sees the
 * work and says "I can help", with a price if they like. No account, and no
 * money moves: the owner reads the bid and gets in touch.
 */
export function OpportunityPageView({ state }: { state: PublicPlanBundle }) {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const [sent, setSent] = useState<{ updated: boolean } | null>(null);

  if (state.status === 'loading') {
    return (
      <Shell title="Opportunity - Redi">
        <View style={styles.centered}>
          <LoadingSpinner />
        </View>
      </Shell>
    );
  }

  if (state.status === 'closed' || state.status === 'error') {
    const closed = state.status === 'closed';
    return (
      <Shell title="Opportunity - Redi">
        <View style={styles.centered} testID={closed ? 'opportunity-closed' : 'opportunity-error'}>
          <EmptyState
            icon={closed ? Link2Off : TriangleAlert}
            title={closed ? "This opportunity isn't open for bids." : 'Something went wrong.'}
            message={
              closed
                ? 'The plan owner may have closed it. Ask them for a new link if you think it should be open.'
                : 'Try again in a moment.'
            }
          />
          <View style={styles.links}>
            <TextLink label="See what REDI is" onPress={() => router.push('/feed')} />
          </View>
        </View>
      </Shell>
    );
  }

  const { planId, bundleId, planTitle, ownerName, bundleName, countLabel, strategies, addresses } =
    state;

  return (
    <Shell title={`${bundleName} - Redi`}>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[
          styles.scrollContent,
          { paddingBottom: insets.bottom + SPACING.xl },
        ]}>
        <View style={styles.column}>
          <Text style={[styles.kicker, { color: colors.textSecondary }]}>
            Wildfire defense work
          </Text>
          <Text style={[styles.title, { color: colors.text }]} testID="opportunity-title">
            {bundleName}
          </Text>
          <Text style={[styles.meta, { color: colors.textSecondary }]} testID="opportunity-plan">
            {`Part of ${planTitle}, by ${ownerName}`}
          </Text>

          <Detail label="The work">
            <Text style={[styles.value, { color: colors.text }]} testID="opportunity-count">
              {countLabel}
            </Text>
            {strategies.length > 0 && (
              <View testID="opportunity-strategies">
                {strategies.map((label) => (
                  <Text key={label} style={[styles.value, { color: colors.text }]}>
                    {label}
                  </Text>
                ))}
              </View>
            )}
          </Detail>

          {addresses.length > 0 && (
            <Detail label="Where">
              <View testID="opportunity-addresses">
                {addresses.slice(0, ADDRESS_LIMIT).map((address) => (
                  <Text key={address} style={[styles.value, { color: colors.text }]}>
                    {address}
                  </Text>
                ))}
                {addresses.length > ADDRESS_LIMIT && (
                  <Text style={[styles.value, { color: colors.textSecondary }]}>
                    {`and ${addresses.length - ADDRESS_LIMIT} more`}
                  </Text>
                )}
              </View>
            </Detail>
          )}

          <View style={styles.links}>
            <TextLink
              label="See the whole plan"
              onPress={() => router.push(`/plan/${planId}` as Href)}
              testID="opportunity-plan-link"
            />
          </View>

          <View style={[styles.card, { borderColor: colors.border }]} testID="opportunity-bid-card">
            {sent ? (
              <View style={styles.success} testID="opportunity-bid-success">
                <CheckCircle2 size={28} color={colors.action} />
                <Text style={[styles.successTitle, { color: colors.text }]}>
                  {sent.updated
                    ? 'Thanks. We updated your bid.'
                    : 'Thanks. The plan owner will see your bid and be in touch.'}
                </Text>
              </View>
            ) : (
              <>
                <Text style={[styles.cardTitle, { color: colors.text }]}>Can you help?</Text>
                <Text style={[styles.cardIntro, { color: colors.textSecondary }]}>
                  Tell the plan owner who you are and, if you can, what it would cost.
                </Text>
                <BidForm
                  planId={planId}
                  bundleId={bundleId}
                  onSent={(result) => setSent({ updated: result.updated })}
                />
              </>
            )}
          </View>
        </View>
      </ScrollView>
    </Shell>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    alignItems: 'center',
    paddingBottom: SPACING.md,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  headerInner: {
    width: '100%',
    maxWidth: READING_MAX_WIDTH,
    paddingHorizontal: SPACING.md,
  },
  centered: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: SPACING.lg,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    alignItems: 'center',
    paddingTop: SPACING.xl,
  },
  column: {
    width: '100%',
    maxWidth: READING_MAX_WIDTH,
    paddingHorizontal: SPACING.md,
  },
  kicker: {
    ...TYPOGRAPHY.overline,
    marginBottom: SPACING.xs,
  },
  title: {
    ...TYPOGRAPHY.screenTitle,
  },
  meta: {
    ...TYPOGRAPHY.bodySmall,
    marginTop: SPACING.xs,
  },
  detail: {
    marginTop: SPACING.lg,
  },
  overline: {
    ...TYPOGRAPHY.overline,
    marginBottom: SPACING.xs,
  },
  value: {
    ...TYPOGRAPHY.body,
  },
  links: {
    marginTop: SPACING.md,
    alignItems: 'flex-start',
    gap: SPACING.sm,
  },
  card: {
    marginTop: SPACING.xl,
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.lg,
    padding: SPACING.lg,
  },
  cardTitle: {
    ...TYPOGRAPHY.h4,
    marginBottom: SPACING.xs,
  },
  cardIntro: {
    ...TYPOGRAPHY.bodySmall,
    marginBottom: SPACING.md,
  },
  success: {
    alignItems: 'center',
    gap: SPACING.md,
  },
  successTitle: {
    ...TYPOGRAPHY.h4,
    textAlign: 'center',
  },
});
